/**
 * UI Store
 *
 * All modal/panel visibility, dark mode, layout state.
 * Changes frequently and independently of simulation state.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type ThemeMode = 'dark' | 'light' | 'auto' | 'night';

/**
 * Apply a theme to <html>. Dark is native (no class); `light` and `night`
 * are variable overrides in index.css; `auto` follows the OS between
 * light and dark.
 */
export function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  root.classList.remove('light', 'night');
  if (mode === 'light') root.classList.add('light');
  else if (mode === 'night') root.classList.add('night');
  else if (mode === 'auto' && window.matchMedia('(prefers-color-scheme: light)').matches) {
    root.classList.add('light');
  }
}

// Auto mode tracks OS changes live.
if (typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    const persisted = useUIStore.getState().theme;
    if (persisted === 'auto') applyTheme('auto');
  });
}

interface UIState {
  // Theme
  theme: ThemeMode;

  // Panels
  isSidebarOpen: boolean;
  isSessionHistoryOpen: boolean;
  isSettingsOpen: boolean;

  // Simulation panels
  activeSimPanel: 'dialog' | 'npcs' | 'scene';
  isTimelineOpen: boolean;
  isActionsOpen: boolean;
  isLawModuleOpen: boolean;
  isInsituOpen: boolean;

  // Modals
  activeModal: string | null;

  // Notifications
  npcChangeCount: number;
  sceneChangeCount: number;
  itemChangeCount: number;
}

interface UIActions {
  setTheme: (mode: ThemeMode) => void;

  toggleSidebar: () => void;
  toggleSessionHistory: () => void;
  toggleSettings: () => void;

  setActiveSimPanel: (panel: UIState['activeSimPanel']) => void;
  toggleTimeline: () => void;
  toggleActions: () => void;
  toggleLawModule: () => void;
  toggleInsitu: () => void;

  openModal: (modalId: string) => void;
  closeModal: () => void;

  incrementNpcChanges: () => void;
  incrementSceneChanges: () => void;
  incrementItemChanges: () => void;
  clearNotifications: (panel: 'npcs' | 'scene' | 'items') => void;
}

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    persist(
      (set) => ({
        theme: 'dark' as ThemeMode,
        isSidebarOpen: true,
        isSessionHistoryOpen: false,
        isSettingsOpen: false,
        activeSimPanel: 'dialog',
        isTimelineOpen: false,
        isActionsOpen: false,
        isLawModuleOpen: false,
        isInsituOpen: false,
        activeModal: null,
        npcChangeCount: 0,
        sceneChangeCount: 0,
        itemChangeCount: 0,

        setTheme: (mode) => {
          applyTheme(mode);
          set({ theme: mode }, false, `theme/${mode}`);
        },

        toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen }), false, 'toggleSidebar'),
        toggleSessionHistory: () => set((s) => ({ isSessionHistoryOpen: !s.isSessionHistoryOpen }), false, 'toggleHistory'),
        toggleSettings: () => set((s) => ({ isSettingsOpen: !s.isSettingsOpen }), false, 'toggleSettings'),

        setActiveSimPanel: (panel) => set({ activeSimPanel: panel }, false, `panel/${panel}`),
        toggleTimeline: () => set((s) => ({ isTimelineOpen: !s.isTimelineOpen }), false, 'toggleTimeline'),
        toggleActions: () => set((s) => ({ isActionsOpen: !s.isActionsOpen }), false, 'toggleActions'),
        toggleLawModule: () => set((s) => ({ isLawModuleOpen: !s.isLawModuleOpen }), false, 'toggleLaw'),
        toggleInsitu: () => set((s) => ({ isInsituOpen: !s.isInsituOpen }), false, 'toggleInsitu'),

        openModal: (modalId) => set({ activeModal: modalId }, false, `modal/${modalId}`),
        closeModal: () => set({ activeModal: null }, false, 'closeModal'),

        incrementNpcChanges: () => set((s) => ({ npcChangeCount: s.npcChangeCount + 1 }), false, 'npcChange'),
        incrementSceneChanges: () => set((s) => ({ sceneChangeCount: s.sceneChangeCount + 1 }), false, 'sceneChange'),
        incrementItemChanges: () => set((s) => ({ itemChangeCount: s.itemChangeCount + 1 }), false, 'itemChange'),

        clearNotifications: (panel) => {
          switch (panel) {
            case 'npcs': set({ npcChangeCount: 0 }, false, 'clearNpc'); break;
            case 'scene': set({ sceneChangeCount: 0 }, false, 'clearScene'); break;
            case 'items': set({ itemChangeCount: 0 }, false, 'clearItems'); break;
          }
        },
      }),
      {
        name: 'copapp-ui',
        version: 2,
        migrate: (persisted: unknown) => {
          // v1 stored isDarkMode: boolean
          const old = persisted as { isDarkMode?: boolean; theme?: ThemeMode; isSidebarOpen?: boolean };
          return {
            theme: old.theme ?? (old.isDarkMode === false ? 'light' : 'dark'),
            isSidebarOpen: old.isSidebarOpen ?? true,
          };
        },
        partialize: (state) => ({
          theme: state.theme,
          isSidebarOpen: state.isSidebarOpen,
        }),
      },
    ),
    { name: 'UIStore' },
  ),
);
